<template>
  <h2 class="mb-4">{{ t('customer') }}</h2>

  <div v-if="!editCustomerData && booking.customerData">
    <div class="border-primary/40 mb-4 border-b pb-4">
      <CustomerOverview
        :customer-data="booking.customerData"
        :account-email="booking.customer?.email"
      />
      <CVButton
        v-if="$can('update', subject('Booking', booking), 'customerData')"
        class="mt-4 max-sm:w-full"
        :disabled="isLoading"
        @click.prevent="editCustomerData = true"
        >{{ t('editCustomerData') }}</CVButton
      >
    </div>

    <div v-if="!isSwappingCustomer" class="grid gap-y-2 md:flex md:items-center md:justify-between">
      <LabeledText :label="t('customerAccount')">
        <div v-if="booking.customer?.email" class="flex items-center gap-2">
          {{ booking.customer.email }}
          <CustomerTierBadge
            :customer-tier="booking.customer.customerTier ?? CustomerTier.NORMAL"
            size="sm"
          />
        </div>
        <div v-else>{{ booking.customer?.email ?? t('noCustomerAccount') }}</div>
      </LabeledText>
      <div
        v-if="booking.customer?.id && $can('update', subject('Booking', booking), 'customer')"
        class="grid gap-2 md:flex md:gap-4"
      >
        <CVButton
          v-if="customerCanBeRemoved"
          variant="danger"
          :is-loading="isLoading"
          @click.prevent="removeAccount"
          >{{ t('removeAccount') }}</CVButton
        >
        <CVButton :disabled="isLoading" @click.prevent="isSwappingCustomer = true">{{
          t('swapAccount')
        }}</CVButton>
        <CVButton
          v-if="$can('update', 'User')"
          :disabled="isLoading"
          @click.prevent="viewAccount"
          >{{ t('viewAccount') }}</CVButton
        >
      </div>
      <div
        v-if="!booking.customer?.id && $can('update', subject('Booking', booking), 'customer')"
        class="grid gap-2 md:flex md:gap-4"
      >
        <CVButton :disabled="isLoading" @click.prevent="isSwappingCustomer = true">{{
          t('linkAccount')
        }}</CVButton>
      </div>
    </div>
  </div>

  <div v-else-if="editCustomerData" class="space-y-4">
    <CustomerDataForm
      hide-header
      contact-email
      :email-tooltip="!booking.customer ? t('emailInformation') : undefined"
    />

    <div
      v-if="$can('update', subject('Booking', booking), 'taxProcedure')"
      class="grid gap-4 md:grid-cols-3"
    >
      <TaxProcedureSelect
        class="col-start-1"
        :model-value="taxProcedure"
        :vat-id="formValues.vatId"
        @update:model-value="handleTaxProcedureUpdate"
      />
    </div>

    <div class="flex justify-end gap-2">
      <CVButton variant="default" :is-loading="isLoading" @click.prevent="onUpdateCustomerData">{{
        t('save')
      }}</CVButton>
      <CVButton
        variant="secondary"
        :disabled="isLoading"
        @click.prevent="editCustomerData = false"
        >{{ t('cancel') }}</CVButton
      >
    </div>
  </div>

  <SwapCustomer
    v-if="isSwappingCustomer"
    :schema="customerValidationSchema"
    :is-saving="isLoading"
    @swap-customer="swapCustomer"
    @cancel="isSwappingCustomer = false"
  />
</template>

<script lang="ts" setup>
import { useI18n } from 'vue-i18n';
import CustomerDataForm from './CustomerDataForm.vue';
import { computed, nextTick, ref, watch } from 'vue';
import CustomerOverview from './CustomerOverview.vue';
import { useForm } from 'vee-validate';
import type { Booking } from '@/entities/bookings/booking.entity';
import SwapCustomer from './SwapCustomer.vue';
import { useRouter } from 'vue-router';
import { customerEditSchema } from '@/validation/customer.schema';
import { useCreateUser } from '@/queries/use-users';
import { Alert } from '@/services/alert.service';
import { Local } from '@/entities/auth/local.enum';
import { toTypedSchema } from '@vee-validate/yup';
import { usersService } from '@/api/users.service';
import { BookingStatus } from '@/entities/bookings/booking-status.enum';
import { subject } from '@casl/ability';
import type { User } from '@/entities/auth/user.entity';
import { useGenerateSwapCustomerDto } from '@/components/booking-forms/customer/use-generate-swap-customer-dto';
import LabeledText from '@/components/LabeledText.vue';
import { CustomerTier, PriceRecalculationMode, TaxProcedure } from '@carvia/ros-client-types';
import CustomerTierBadge from '@/components/badges/CustomerTierBadge.vue';
import { useUpdateBooking } from '@/queries/use-bookings.ts';
import { isEqual } from 'lodash';
import { useVatIdValidation } from '@/hooks/use-vat-id-validation';
import TaxProcedureSelect from '@/components/TaxProcedureSelect.vue';
import { useSuggestedTaxProcedure } from '@/hooks/use-suggested-tax-procedure';
import { usePriceChangeConfirmation } from '@/components/price-change-confirmation/price-change-confirmation';
import { fromCalendarDate } from '@/utils/calendar-date';

const props = defineProps<{
  booking: Booking;
}>();

const { t } = useI18n();
const router = useRouter();

const editCustomerData = ref(false);
const isSwappingCustomer = ref(false);

const { confirmAndSave, isBusy } = usePriceChangeConfirmation();
const { isPending: isUpdatingBookingDirect, mutateAsync: updateBooking } = useUpdateBooking();
const { mutateAsync: createUser, isPending: isCreatingUser } = useCreateUser();
const { isValidatingVatId, validateVatId } = useVatIdValidation();

const isLoading = computed(
  () =>
    isBusy.value ||
    isUpdatingBookingDirect.value ||
    isCreatingUser.value ||
    isValidatingVatId.value,
);

const customerCanBeRemoved = computed(
  () =>
    props.booking.status === BookingStatus.DRAFT ||
    props.booking.status === BookingStatus.OPEN ||
    props.booking.status === BookingStatus.CONFIRMED ||
    props.booking.status === BookingStatus.FLOATING,
);

const initializeFormValues = () => {
  return {
    ...props.booking.customerData,
    dateOfBirth: props.booking.customerData?.dateOfBirth
      ? fromCalendarDate(props.booking.customerData.dateOfBirth)
      : undefined,
    contactEmail:
      props.booking.customerData?.contactEmail?.trim() ||
      props.booking.customer?.email?.trim() ||
      undefined,
  };
};

const taxProcedure = ref<TaxProcedure>(props.booking.taxProcedure ?? TaxProcedure.REGULAR);

const customerValidationSchema = computed(() =>
  customerEditSchema(
    computed(() => props.booking.bookedCar.vehicleType.minAge),
    computed(() => props.booking.startDate),
    computed(
      () =>
        !props.booking.customer &&
        editCustomerData.value &&
        !props.booking.insuranceCase &&
        !props.booking.agency,
    ),
    computed(() => !editCustomerData.value),
    taxProcedure,
  ),
);

const validationSchema = computed(() => toTypedSchema(customerValidationSchema.value));

const {
  handleSubmit,
  setValues,
  values: formValues,
} = useForm({
  initialValues: initializeFormValues(),
  validationSchema,
});

const { setTaxProcedure } = useSuggestedTaxProcedure({
  mode: 'edit',
  taxProcedure,
  startDate: () => props.booking.startDate,
  endDate: () => props.booking.endDate,
  vatId: () => formValues.vatId,
  country: () => formValues.country,
  initialTaxProcedure: () => props.booking.taxProcedure,
});

const handleTaxProcedureUpdate = (value: TaxProcedure) => {
  setTaxProcedure(value, true);
};

watch(
  () => props.booking.customerData,
  (newValue, oldValue) => {
    if (!isEqual(newValue, oldValue)) {
      setValues(initializeFormValues());
    }
  },
  { immediate: true },
);

watch(editCustomerData, async (isEditing) => {
  if (!isEditing) return;
  await nextTick();
  setValues(initializeFormValues());
});

const onUpdateCustomerData = handleSubmit(async (values) => {
  const customerData = values;
  const contactEmail = customerData.contactEmail;
  if (
    !(await validateVatId(
      customerData.vatId,
      props.booking.customerData?.vatId,
      customerData.country,
    ))
  )
    return;

  let customerId: string | null | undefined = props.booking.customer?.id;

  if (customerId && !contactEmail?.trim()) {
    if (customerCanBeRemoved.value) {
      const { isConfirmed } = await Alert.fire({
        text: `${t('emptyContactEmailFallsBackText')} ${t('removeOrKeepAccountText')}`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: t('removeAccount'),
        cancelButtonText: t('keepAccount'),
      });
      if (isConfirmed) {
        customerId = null;
      }
    } else {
      await Alert.fire({
        text: t('emptyContactEmailFallsBackText'),
        icon: 'info',
        confirmButtonText: t('ok'),
      });
    }
  } else if (!customerId) {
    const { emailExists, existingUser } = await checkIfEmailExists(contactEmail);
    customerId = existingUser?.id;
    if (!customerId && contactEmail) {
      if (emailExists) return;
      const { contactEmail: accountEmail, ...customerDataForUser } = customerData;
      const { id } = existingUser
        ? existingUser
        : await createUser({
            ...customerDataForUser,
            preferredLocal: customerData.preferredLocal ?? Local.EN_US,
            email: accountEmail!,
          });
      customerId = id;
    }
  }

  const taxProcedureChanged =
    taxProcedure.value !== (props.booking.taxProcedure ?? TaxProcedure.REGULAR);
  const bookingUpdate = { customerData, customerId, taxProcedure: taxProcedure.value };

  if (taxProcedureChanged) {
    const { status } = await confirmAndSave({
      booking: props.booking,
      updateBookingDto: {
        ...bookingUpdate,
        pricing: {
          priceRecalculationMode: PriceRecalculationMode.RECALCULATE,
        },
      },
      allowKeepOldPrice: true,
    });
    if (status !== 'saved') {
      return;
    }
  } else {
    await updateBooking({
      id: props.booking.id,
      booking: bookingUpdate,
    });
  }

  editCustomerData.value = false;
});

const checkIfEmailExists = async (email?: string) => {
  if (props.booking.customer || !email)
    return {
      emailExists: false,
      existingUser: null,
    };
  const user = await usersService.getOneByEmailIfExists(email);
  if (!user?.id) {
    return {
      emailExists: false,
      existingUser: null,
    };
  }
  const { isConfirmed } = await Alert.fire({
    titleText: t('emailExistsTitle'),
    html: t('emailExistsText', {
      name: user.firstName + ' ' + user.lastName,
      customerNumber: user.customerNumber,
      email: user.email,
    }),
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: t('useAccount'),
    cancelButtonText: t('dontUse'),
  });

  return {
    emailExists: true,
    existingUser: isConfirmed ? user : null,
  };
};

const generateSwapCustomerDto = useGenerateSwapCustomerDto();
const swapCustomer = async (customer: User) => {
  await updateBooking({
    id: props.booking.id,
    booking: await generateSwapCustomerDto(customer),
  });
  isSwappingCustomer.value = false;
};

const viewAccount = () => {
  if (!props.booking.customer?.id) return;
  router.push({
    name: 'user',
    params: { id: props.booking.customer?.id },
  });
};

const removeAccount = async () => {
  const { isConfirmed } = await Alert.fire({
    text: t('removeAccountText'),
    icon: 'warning',
    showCancelButton: true,
    confirmButtonText: t('removeAccount'),
    cancelButtonText: t('cancel'),
  });

  if (isConfirmed) {
    await updateBooking({
      id: props.booking.id,
      booking: { customerId: null },
    });
  }
};
</script>

<i18n lang="json">
{
  "en": {
    "customer": "Customer",
    "editCustomerData": "Edit customer data for this booking",
    "swapAccount": "Swap Account",
    "viewAccount": "View Account",
    "swap": "Swap",
    "createNewCustomer": "+ Create new customer",
    "customerAccount": "Customer account",
    "noCustomerAccount": "No customer account linked",
    "emailInformation": "The email will be used to create a new customer account. The contract, car-swap protocol, and NPS survey will be sent to this address. Please double check if the email is correct.",
    "contactEmail": "Contact email",
    "contactEmailInformation": "The contract, car-swap protocol, and NPS survey will be sent to this address. Invoices and payment links use the linked customer account email.",
    "emailExistsTitle": "Email already exists",
    "emailExistsText": "<div class='font-medium mb-3'>{email}<br/>{name} (Cnr. {customerNumber})</div>Do you want to use this existing account for the booking?",
    "useAccount": "Use Account",
    "dontUse": "Don't use",
    "email": "Email",
    "removeAccount": "Remove Account",
    "removeAccountText": "Do you really want to remove the linked customer account?",
    "emptyContactEmailFallsBackText": "Clearing the contact email will fall back to the linked customer account email.",
    "removeOrKeepAccountText": "Do you want to keep or remove the linked customer account?",
    "keepAccount": "Keep Account",
    "linkAccount": "Link Account"
  },
  "de": {
    "customer": "Kunde",
    "editCustomerData": "Kundendaten dieser Buchung bearbeiten",
    "swapAccount": "Konto austauschen",
    "viewAccount": "Konto ansehen",
    "swap": "Austauschen",
    "createNewCustomer": "+ Neuen Kunden anlegen",
    "customerAccount": "Kundenkonto",
    "noCustomerAccount": "Kein verknüpftes Kundenkonto",
    "emailInformation": "Die Email wird verwendet um einen neuen Kundenaccount zu erstellen. Vertrag, Tauschprotokoll und NPS-Umfrage werden an diese Adresse gesendet. Bitte stelle sicher, dass die Email korrekt ist.",
    "contactEmail": "Kontakt-E-Mail",
    "contactEmailInformation": "Vertrag, Tauschprotokoll und NPS-Umfrage werden an diese Adresse gesendet. Rechnungen und Zahlungslinks nutzen die E-Mail des verknüpften Kundenkontos.",
    "emailExistsTitle": "Email existiert bereits",
    "emailExistsText": "<div class='font-medium mb-3'>{email}<br/>{name} (Knr. {customerNumber})</div>Möchtest du dieses bestehende Konto für die Buchung verwenden?",
    "useAccount": "Konto verwenden",
    "dontUse": "Nicht verwenden",
    "email": "Email",
    "removeAccount": "Konto entfernen",
    "removeAccountText": "Möchtest du das verknüpfte Kundenkonto wirklich entfernen?",
    "emptyContactEmailFallsBackText": "Wenn die Kontakt-E-Mail geleert wird, wird die E-Mail des verknüpften Kundenkontos verwendet.",
    "removeOrKeepAccountText": "Möchtest du das verknüpfte Kundenkonto behalten oder entfernen?",
    "keepAccount": "Konto behalten",
    "linkAccount": "Konto verknüpfen"
  },
  "es": {
    "customer": "Cliente",
    "editCustomerData": "Editar datos del cliente en esta reserva",
    "swapAccount": "Cambiar cuenta",
    "viewAccount": "Ver cuenta",
    "swap": "Cambiar",
    "createNewCustomer": "+ Crear cliente nuevo",
    "customerAccount": "Cuenta de cliente",
    "noCustomerAccount": "Sin cuenta de cliente vinculada",
    "emailInformation": "El correo se usará para crear una cuenta de cliente nueva. El contrato, el protocolo de cambio de coche y la encuesta NPS se enviarán a esta dirección. Compruebe que el correo sea correcto.",
    "contactEmail": "Correo de contacto",
    "contactEmailInformation": "El contrato, el protocolo de cambio de coche y la encuesta NPS se enviarán a esta dirección. Las facturas y los enlaces de pago usan el correo de la cuenta vinculada.",
    "emailExistsTitle": "El correo ya existe",
    "emailExistsText": "<div class='font-medium mb-3'>{email}<br/>{name} (N.º cliente {customerNumber})</div>¿Desea usar esta cuenta existente para la reserva?",
    "useAccount": "Usar cuenta",
    "dontUse": "No usar",
    "email": "Correo electrónico",
    "removeAccount": "Quitar cuenta",
    "removeAccountText": "¿Seguro que desea quitar la cuenta de cliente vinculada?",
    "emptyContactEmailFallsBackText": "Si borra el correo de contacto, se usará el correo de la cuenta de cliente vinculada.",
    "removeOrKeepAccountText": "¿Desea mantener o quitar la cuenta de cliente vinculada?",
    "keepAccount": "Mantener cuenta",
    "linkAccount": "Vincular cuenta"
  }
}
</i18n>
